import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ChevronRight, PlusCircle } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import api from "@/lib/api";

const STATUS = {
  under_review: { label: "Under review", cls: "bg-[#EF9F27]/20 text-[#EF9F27]" },
  opinion_ready: { label: "Opinion ready", cls: "bg-[#16A34A]/20 text-[#22C55E]" },
  pending: { label: "Under review", cls: "bg-[#EF9F27]/20 text-[#EF9F27]" },
};

export default function Cases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/care/cases").then(({ data }) => setCases(data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <CareShell active="cases" title="My cases">
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : cases.length === 0 ? (
        <div className="mt-6 bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-8 text-center">
          <Stethoscope className="mx-auto text-[#666666]" size={32} />
          <p className="mt-3 text-[#F8FAFC] font-bold">No cases yet</p>
          <p className="text-sm text-[#A1A1AA] mt-1">Start a specialist review to get a second opinion.</p>
          <button
            data-testid="cases-start-btn"
            onClick={() => navigate("/care/new")}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-5 py-3 min-h-[44px] transition-transform active:scale-95"
          >
            <PlusCircle size={18} /> New review
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {cases.map((c) => (
            <li key={c.id}>
              <button
                data-testid={`cases-item-${c.id}`}
                onClick={() => navigate(`/care/cases/${c.id}`)}
                className="w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5 hover:bg-[#222222] transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-head text-lg font-bold text-[#F8FAFC] truncate">{c.title}</h3>
                    <p className="text-sm text-[#EF9F27]">{c.specialty}</p>
                  </div>
                  <ChevronRight className="text-[#666666] shrink-0" />
                </div>
                <p className="text-sm text-[#A1A1AA] mt-2 line-clamp-2">{c.summary}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wide ${(STATUS[c.status] || STATUS.opinion_ready).cls}`}>
                    {(STATUS[c.status] || { label: c.status }).label}
                  </span>
                  <span className="text-xs text-[#666666]">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </CareShell>
  );
}
